// Design System Variables - Material UI Theme Integration
// This file contains the design variables provided by the designer
// and maps them to Material UI's theme structure

// Design System Colors - Only color definitions from the designer
export const designVariables = {
  // Colors - Emerald/Green
  color: {
    emerald: {
      41: "#24AE81",
      84: "#B8F2E2",
      39: "#10B981",
      67: "#6EE7B7",
      45: "#13D39E",
      11: "#043225",
      36: "#64d9b8",
      63: "#6BE7C5", // Light emerald for text and borders
      12: "rgba(19, 211, 158, 0.12)", // 12% opacity version of emerald-45
    },

    // Colors - Grey
    grey: {
      98: "#F9F9F9",
      96: "#F1F5F9",
      19: "#2E3334",
      11: "#1B1C1C", // Dark grey for disabled elements
      54: "#545958", // New color for disabled text
      muted: "#E0E9E6", // Light greyish-green for text buttons and disabled states
    },

    // Colors - Orange
    orange: {
      50: "#F59E0B",
    },

    // Colors - Azure/Blue
    azure: {
      60: "#3B82F6",
      65: "#94A3B8",
    },

    // Colors - White
    white: {
      solid: "#FFFFFF",
      "8%": "rgba(255, 255, 255, 0.08)",
      "12%": "rgba(255, 255, 255, 0.12)",
    },

    yellow: {
      1: "#C4F61B",
      2: "#4B6100",
    },
  },
};

// Material UI theme colors mapping
export const muiColors = {
  // Primary color palette (emerald/green)
  primary: {
    light: designVariables.color.emerald[84], // #B8F2E2 - Light emerald
    main: designVariables.color.emerald[45], // #13D39E - Main emerald
    lightMain: designVariables.color.yellow[2], // #4B6100
    dark: designVariables.color.emerald[11], // #043225 - Dark emerald
    contrastText: designVariables.color.white.solid, // #FFFFFF
    // Additional shades for comprehensive theming
    50: designVariables.color.emerald[84], // #B8F2E2
    100: designVariables.color.emerald[67], // #6EE7B7
    200: designVariables.color.emerald[45], // #13D39E
    300: designVariables.color.emerald[41], // #24AE81
    400: designVariables.color.emerald[39], // #10B981
    500: designVariables.color.emerald[36], // #0FA97E
    600: designVariables.color.emerald[11], // #043225
  },

  // Secondary color palette (using different emerald shades)
  secondary: {
    light: designVariables.color.emerald[67], // #6EE7B7
    main: designVariables.color.emerald[36], // #0FA97E
    dark: designVariables.color.emerald[39], // #10B981
    contrastText: designVariables.color.white.solid, // #FFFFFF
  },

  // Error color (keeping existing or using a red if needed)
  error: {
    light: "#ffcdd2",
    main: "#f44336",
    dark: "#d32f2f",
    contrastText: designVariables.color.white.solid,
  },

  // Warning color (using orange from design system)
  warning: {
    light: "#fff3c4",
    main: designVariables.color.orange[50], // #F59E0B
    dark: "#ff8f00",
    contrastText: designVariables.color.white.solid,
  },

  // Info color (using azure/blue from design system)
  info: {
    light: "#bbdefb",
    main: designVariables.color.azure[60], // #3B82F6
    dark: "#1976d2",
    contrastText: designVariables.color.white.solid,
  },

  // Success color (using emerald)
  success: {
    light: designVariables.color.emerald[84], // #B8F2E2
    main: designVariables.color.emerald[39], // #10B981
    dark: designVariables.color.emerald[11], // #043225
    contrastText: designVariables.color.white.solid,
  },

  // Grey palette for backgrounds, text, etc.
  grey: {
    50: designVariables.color.white.solid, // #FFFFFF
    100: designVariables.color.grey[98], // #F9F9F9
    200: designVariables.color.grey[96], // #F1F5F9
    300: "#e0e0e0",
    400: "#bdbdbd",
    500: "#9e9e9e",
    600: "#757575",
    700: "#616161",
    800: designVariables.color.azure[65], // #94A3B8
    900: designVariables.color.grey[19], // #2E3334
  },

  // Background colors
  background: {
    default: designVariables.color.grey[96], // #F1F5F9 for light mode
    paper: designVariables.color.white.solid, // #FFFFFF
    defaultDark: designVariables.color.grey[19], // #2E3334 for dark mode
    paperDark: "#141A1B", // Slightly lighter than main dark
  },

  // Text colors
  text: {
    primary: designVariables.color.grey[19], // #2E3334 for dark text
    secondary: designVariables.color.azure[65], // #94A3B8 for secondary text
    primaryDark: designVariables.color.grey[98], // #F9F9F9 for dark mode
    secondaryDark: designVariables.color.azure[65], // #94A3B8
    disabled: designVariables.color.grey[54], // #545958 for disabled text
  },

  // Additional semantic colors
  divider: "#e2e8f0",
  dividerDark: "rgba(255, 255, 255, 0.12)",

  // Button specific colors
  button: {
    outlined: {
      disabled: {
        text: designVariables.color.grey.muted, // #E0E9E6
        border: designVariables.color.grey.muted, // #E0E9E6
        background: "transparent",
      },
    },
    text: {
      primary: designVariables.color.grey.muted, // #E0E9E6 for text buttons
    },
  },
};

// CSS custom properties for colors only
export const cssVariables = `
:root {
  /* Colors - Emerald */
  --color-emerald-41: ${designVariables.color.emerald[41]};
  --color-emerald-84: ${designVariables.color.emerald[84]};
  --color-emerald-39: ${designVariables.color.emerald[39]};
  --color-emerald-67: ${designVariables.color.emerald[67]};
  --color-emerald-45: ${designVariables.color.emerald[45]};
  --color-emerald-11: ${designVariables.color.emerald[11]};
  --color-emerald-36: ${designVariables.color.emerald[36]};
  --color-emerald-63: ${designVariables.color.emerald[63]};
  --color-emerald-12: ${designVariables.color.emerald[12]};
  
  /* Colors - Grey */
  --color-grey-98: ${designVariables.color.grey[98]};
  --color-grey-96: ${designVariables.color.grey[96]};
  --color-grey-19: ${designVariables.color.grey[19]};
  --color-grey-54: ${designVariables.color.grey[54]};
  --color-grey-11: ${designVariables.color.grey[11]};
  --color-grey-muted: ${designVariables.color.grey.muted};
  
  /* Colors - Orange */
  --color-orange-50: ${designVariables.color.orange[50]};
  
  /* Colors - Azure */
  --color-azure-60: ${designVariables.color.azure[60]};
  --color-azure-65: ${designVariables.color.azure[65]};
  
  /* Colors - White */
  --color-white-solid: ${designVariables.color.white.solid};
  --color-white-8: ${designVariables.color.white["8%"]};
  --color-white-12: ${designVariables.color.white["12%"]};
}
`;

export default {
  designVariables,
  muiColors,
  cssVariables,
};
